import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useUnitSettings } from '../contexts/UnitSettingsContext';
import LightsControl from '../components/LightsControl';
import { useFarmStatus } from '../contexts/FarmStatusContext';
import api, {
  plantsAPI, roomAPI, meterAPI, nutrientsAPI, rackPhotosAPI, farmLogAPI, weatherAPI,
} from '../services/api';
import { SYSTEMS } from '../config/systems';
import { isFresh } from '../config/freshness';
import { todayPace } from '../config/meter';
import { Panel, PanelTitle, Skel } from '../components/dashboard/ui';
import Hero from '../components/dashboard/Hero';
import ContainerCard from '../components/dashboard/ContainerCard';
import PlantHealthCard from '../components/dashboard/PlantHealthCard';
import GrowthCard from '../components/dashboard/GrowthCard';
import WeatherCard from '../components/dashboard/WeatherCard';
import TanksCard from '../components/dashboard/TanksCard';
import PowerTodayCard from '../components/dashboard/PowerTodayCard';
import RackPhotosStrip from '../components/dashboard/RackPhotosStrip';
import NeedsYou from '../components/dashboard/NeedsYou';
import TodayCard from '../components/dashboard/TodayCard';
import OpenIssuesCard from '../components/dashboard/OpenIssuesCard';

/*
 * The farm overview: one screen that answers "is everything okay, and if not,
 * what do I do?" without visiting the other pages.
 *
 * Top to bottom: a hero with the verdict sentence and the newest real photo; one
 * tile per system (tanks, climate, power, lights, devices, crop); then what needs a
 * person, open issues, today's routine, the tanks, the lights, photos and the crop.
 *
 * The problem rules live in FarmStatusContext, shared with the sidebar's badges,
 * so the two never disagree.
 */

const CONTEXT_REFRESH_MS = 60000;

// Weather is polled every 15 min; two hours without a row means the poller broke.
const WEATHER_STALE_AFTER_SECONDS = 2 * 3600;

/* ------------------------------------------------------------------ layout */

// Capped so the cards stop growing on a wide screen; the extra width becomes margin.
const Page = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  font-size: 13px;
  line-height: 1.45;
  font-variant-numeric: tabular-nums;
  color: ${p => p.theme.colors.text};
`;

const Top = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
  gap: 16px;

  @media (max-width: 1100px) { grid-template-columns: minmax(0, 1fr); }
`;

/* Two columns that stack independently on desktop (no row alignment gaps), and
   one column in reading order on a phone: `display: contents` lets each panel's
   `order` interleave the two columns. */
const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 1100px) { display: flex; flex-direction: column; align-items: stretch; }
`;

const Col = styled.div`
  display: grid;
  gap: 16px;
  min-width: 0;
  @media (max-width: 1100px) { display: contents; }
`;

/* ------------------------------------------------------------------ at a glance */

/* Health, growth and weather: the three questions anyone looking at a farm asks
   first, each answered in one card. */
const Glance = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) { grid-template-columns: minmax(0, 1fr); }
`;

/* ------------------------------------------------------------------ page */

const ALL_UNITS = SYSTEMS.map(({ id, name }) => ({ id, name }));
const UNIT_NAMES = Object.fromEntries(ALL_UNITS.map(u => [u.id, u.name]));

const Dashboard = () => {
  const status = useFarmStatus();
  const { now, ranges, tanks, climate, lightSum, devices, crop, items, setLights } = status;
  const { isUnitEnabled } = useUnitSettings();
  const [extra, setExtra] = useState({});
  const [extraLoaded, setExtraLoaded] = useState(false);

  // What only this page shows (photos, today's routine, sparklines). The status
  // every page shares (tanks, climate, devices, problems) comes from FarmStatusContext.
  useEffect(() => {
    let cancelled = false;
    const sources = {
      profile: () => meterAPI.getDayProfile().then(r => r.data),
      manager: () => farmLogAPI.getManagerToday().then(r => r.data?.log || null),
      doses: () => nutrientsAPI.getDoses({ limit: 60 }).then(r => r.data?.doses || []),
      rackPhotos: () => rackPhotosAPI.list({ days: 7 }).then(r => r.data?.days || []),
      logs: () => plantsAPI.getRecentLogs(60).then(r => r.data?.logs || []),
      weather: () => api.get('/weather/current').then(r => r.data?.outside || null),
      climate6h: () => roomAPI.getClimate('6h').then(r => r.data),
      weather24: () => weatherAPI.getHistory('24h').then(r => r.data?.data || []),
    };
    const load = async () => {
      const keys = Object.keys(sources);
      const results = await Promise.allSettled(keys.map(k => sources[k]()));
      if (cancelled) return;
      setExtra((prev) => {
        const next = { ...prev };
        results.forEach((res, i) => { if (res.status === 'fulfilled') next[keys[i]] = res.value; });
        return next;
      });
      setExtraLoaded(true);
    };
    load();
    const id = setInterval(load, CONTEXT_REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const ctx = useMemo(() => ({ ...status.data, ...extra }), [status.data, extra]);
  const loaded = status.loaded && extraLoaded;
  const power = useMemo(() => (status.power
    ? (() => { const tp = todayPace(ctx.profile, status.power.soFar, now); return { ...status.power, pace: tp.pace, paceBasis: tp.basis }; })()
    : null), [status.power, ctx.profile, now]);

  if (!loaded) {
    return (
      <Page aria-busy="true">
        <Top><Skel $h={300} /><Skel $h={300} /></Top>
        <Body>
          <Col><Skel $h={240} /><Skel $h={220} /></Col>
          <Col><Skel $h={200} /><Skel $h={220} /></Col>
        </Body>
      </Page>
    );
  }

  // Fresh outside weather, shared by the Container and Weather cards.
  const outside = ctx.weather && isFresh(ctx.weather.timestamp, WEATHER_STALE_AFTER_SECONDS) ? ctx.weather : null;

  return (
    <Page>
      <Top>
        <Hero crop={crop} items={items} tanks={tanks} lightSum={lightSum} power={power} devices={devices} manager={ctx.manager} now={now} />
        <ContainerCard climate={climate} climate6h={ctx.climate6h} dehum={status.data.dehum} outside={outside} now={now} />
      </Top>

      <Glance>
        <PlantHealthCard crop={crop} />
        <GrowthCard crop={crop} />
        <WeatherCard outside={outside} weather24={ctx.weather24} climate={climate} now={now} />
      </Glance>

      <Body>
        <Col>
          <Panel $order={4}>
            <PanelTitle>
              <span>Grow lights{lightSum.ids.length > 0 && <span className="count">{lightSum.on.length} of {lightSum.ids.length} on</span>}</span>
              <span className="aside">Tap a rack to change it</span>
            </PanelTitle>
            <LightsControl unitNames={UNIT_NAMES} tiles onUnits={setLights} />
          </Panel>
          <TanksCard tanks={tanks} ranges={ranges} water={ctx.water} now={now} order={5} />
          <PowerTodayCard power={power} profile={ctx.profile} monthFrom={status.data.meter?.energy?.month_from} now={now} order={6} />
          <RackPhotosStrip rackPhotos={ctx.rackPhotos} logs={ctx.logs} now={now} order={7} />
        </Col>

        <Col>
          <NeedsYou items={items} order={2} />
          {/* Same order as Today: on a phone the column's DOM order puts it just after Needs you. */}
          <OpenIssuesCard issues={status.data.issues} now={now} order={3} />
          <TodayCard ctx={ctx} lights={status.lights} tanks={tanks} now={now} isUnitEnabled={isUnitEnabled} order={3} />
        </Col>
      </Body>
    </Page>
  );
};

export default Dashboard;
